import {ActivatedRoute, Router} from '@angular/router';

import {Subscription} from 'rxjs';

import {
    ApiError,
    HttpStatusCodesEnum,
    PermissionModel,
    ServerResponseData,
    TradingAccountAccessModel,
    UserWithPermissionsModel,
    SupervisedUsersCollection
} from '@spa/api';

import {CacheCollectionService} from '../../../shared/services';
import {PermissionRuleInterface} from '../../../shared/interfaces';
import {InitialComponentDataValidationException} from '../../../shared/exceptions';
import {ColumnSizesEnum, PermissionRuleCheckStrategiesEnum, PermissionsEnum} from '../../../shared/enums';

import {EventsService} from '../services';
import {LayoutConfigInterface} from '../components';
import {ClientAccessRightsResolverDataInterface} from '../interfaces';
import {ClientAccessRightsResolverErrorStatesEnum, TradingAccountTypesEnum} from '../enums';

import {ClientAccessRightsMessages} from '../../../../assets/messages';

import {BaseComponent} from './base-component';

export abstract class ClientAccessRightsComponent extends BaseComponent {
    private activatedRouteSubscription: Subscription;

    public clientRelationType: number;
    public clientOrClientUserId: number;
    public portalPermissions: PermissionModel[];
    public userWithPermissions: UserWithPermissionsModel;
    public assignedCashAccountsAccessRights: TradingAccountAccessModel[];
    public assignedTradingAccountsAccessRights: TradingAccountAccessModel[];
    public supervisedUsers: SupervisedUsersCollection;

    public updateAccessRightsPermissionRule: PermissionRuleInterface;

    public accountTypes: typeof TradingAccountTypesEnum;

    public getAccessRightsFailedMessage: string;
    public updateAccessRightsFailedMessage: string;
    public getSupervisedRightsFailedMessage: string;
    public updateUserPermissionsFailedMessage: string;
    public updateSupervisedRightsFailedMessage: string;
    public unAssignSupervisedRightsFailedMessage: string;

    public hasNotInitialError: boolean;

    protected constructor(
        protected router: Router,
        protected activatedRoute: ActivatedRoute,
        protected cacheCollectionService: CacheCollectionService,
        public eventsService: EventsService
    ) {
        super(eventsService, router, cacheCollectionService);

        this.updateAccessRightsPermissionRule = {
            permissions: [PermissionsEnum.CrmUsersUpdateRights],
            strategy: PermissionRuleCheckStrategiesEnum.Intersect
        };

        this.accountTypes = TradingAccountTypesEnum;

        this.updateAccessRightsFailedMessage = ClientAccessRightsMessages.UpdateAccountRightsFailed.message;
        this.getSupervisedRightsFailedMessage = ClientAccessRightsMessages.GetSupervisedRightsFailed.message;
        this.getAccessRightsFailedMessage = ClientAccessRightsMessages.GetAssignedAccountsRightsFailed.message;
        this.updateUserPermissionsFailedMessage = ClientAccessRightsMessages.UpdateUserPermissionsFailed.message;
        this.updateSupervisedRightsFailedMessage = ClientAccessRightsMessages.UpdateSupervisedRightsFailed.message;
        this.unAssignSupervisedRightsFailedMessage = ClientAccessRightsMessages.UnAssignSupervisedRightsFailed.message;

        this.hasNotInitialError = false;
    }

    onInit(): void {
        super.onInit();

        this.activatedRouteSubscription = this.activatedRoute.data
            .subscribe((data: { resolverData: ServerResponseData<ClientAccessRightsResolverDataInterface> }) => {
                this.doInitialDataValidationCheck(data.resolverData, () => {
                    this.hasNotInitialError = true;

                    const resolverData = data.resolverData.data as ClientAccessRightsResolverDataInterface;

                    this.portalPermissions = resolverData.portalPermissions;
                    this.userWithPermissions = resolverData.userWithPermissions;

                    this.clientRelationType = resolverData.userRelationType;
                    this.clientOrClientUserId = resolverData.clientOrClientUserId;

                    this.supervisedUsers = resolverData.supervisedUsers;

                    this.assignedTradingAccountsAccessRights = resolverData.assignedAccountsAccessRights
                        .filter((accountAccess: TradingAccountAccessModel) => !accountAccess.isCashAccount);

                    this.assignedCashAccountsAccessRights = resolverData.assignedAccountsAccessRights
                        .filter((accountAccess: TradingAccountAccessModel) => accountAccess.isCashAccount);
                });
            });
    }


    afterViewInit(): void {
        super.afterViewInit();
    }

    onDestroy(): void {
        super.onDestroy();

        if (this.activatedRouteSubscription) {
            this.activatedRouteSubscription.unsubscribe();
        }
    }

    getTitle(): string {
        return '';
    }

    getText(): string {
        return '';
    }

    getLayoutConfig(): LayoutConfigInterface {
        return {
            mainColumnSize: ColumnSizesEnum.Col12,
            subColumnSize: ColumnSizesEnum.Col0
        };
    }

    validateInitialData(response: ServerResponseData<ClientAccessRightsResolverDataInterface>): void {
        if (response.status !== HttpStatusCodesEnum.Ok) {
            const messages = (response.data as ApiError[]).map((error: ApiError) => {
                switch (error.status) {
                    case ClientAccessRightsResolverErrorStatesEnum.GetClientFailed:
                        return ClientAccessRightsMessages.GetClientFailed.message;
                    case ClientAccessRightsResolverErrorStatesEnum.GetUserWithPermissionsFailed:
                        return ClientAccessRightsMessages.GetUserWithPermissionsFailed.message;
                    case ClientAccessRightsResolverErrorStatesEnum.GetPortalPermissionsFailed:
                        return ClientAccessRightsMessages.GetPortalPermissionsFailed.message;
                    case ClientAccessRightsResolverErrorStatesEnum.GetPortalPermissionsNoData:
                        return ClientAccessRightsMessages.GetPortalPermissionsNoData.message;
                    case ClientAccessRightsResolverErrorStatesEnum.GetAssignedAccountsRightsFailed:
                        return ClientAccessRightsMessages.GetAssignedAccountsRightsFailed.message;
                    case ClientAccessRightsResolverErrorStatesEnum.NonActiveClient:
                        return ClientAccessRightsMessages.NonActiveClient.message;
                    default:
                        return ClientAccessRightsMessages.UnknownError.message;
                }
            });

            throw new InitialComponentDataValidationException(messages);
        }
    }
}
